/* eslint-disable @eslint-react/no-array-index-key */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useEventListener } from 'usehooks-ts';
import { WeatherStationHistoricalData } from 'features/weatherStations/weatherStations.types';
import { useWeatherStationState } from 'state/useWeatherStationState';
import { scrollShadowSx, tableSx } from './WeatherStationDataTable.styles';
import { EMPTY_DATA_VALUE } from '../../../../constants';

type WeatherStationDataTableProps = {
  data: WeatherStationHistoricalData;
};

const EmptyDataDash = (): JSX.Element => (
  <Box
    sx={{
      backgroundColor: (theme) => theme.palette.divider,
      width: 16,
      height: 3,
      margin: '0 auto',
    }}
  />
);

// Hook to detect scroll position and determine when to show left/right shadows
// specific to the weather station data table since it uses RTL scrolling.
const useTableScrollShadows = (): {
  containerRef: React.RefObject<HTMLDivElement>;
  showLeftShadow: boolean;
  showRightShadow: boolean;
} => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const checkScroll = useCallback((): void => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    // In RTL mode, scrollLeft starts at 0 when scrolled right and becomes negative when scrolling left
    // At scrollLeft=0: we're at the right edge, so show left shadow (content to the left)
    // At scrollLeft=-(scrollWidth-clientWidth): we're at the left edge, so show right shadow (content to the right)
    setShowLeftShadow(
      isScrollable && scrollLeft > -(scrollWidth - clientWidth) + 1,
    );
    setShowRightShadow(scrollLeft < 0);
  }, []);

  useLayoutEffect(() => {
    checkScroll();
  }, [checkScroll]);

  useEventListener('scroll', checkScroll, containerRef);
  useEventListener('resize', checkScroll);

  return { containerRef, showLeftShadow, showRightShadow };
};

export const WeatherStationDataTable = (
  props: WeatherStationDataTableProps,
): JSX.Element => {
  const {
    data: { head, data },
  } = props;

  const { focusedSensorType } = useWeatherStationState();

  const { containerRef, showLeftShadow, showRightShadow } =
    useTableScrollShadows();

  return (
    <Box
      sx={[
        scrollShadowSx,
        {
          position: 'relative',
          width: '100%',
          '&::before': { opacity: showLeftShadow ? 1 : 0 },
          '&::after': { opacity: showRightShadow ? 1 : 0 },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      ]}
    >
      <Box sx={{ direction: 'rtl' }}>
        <TableContainer
          ref={containerRef}
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            // Hide scrollbar on touch devices, show on devices with mouse
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '@media (pointer: fine)': {
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { display: 'block', height: 8 },
            },
          }}
        >
          <Table size="small" sx={{ ...tableSx, direction: 'ltr' }}>
            <TableHead>
              <TableRow>
                {head.map((value, index) => (
                  <TableCell key={`${head}-${index}`}>{value}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, rowIndex) => {
                if (row.rowType === 'separator') {
                  return (
                    <TableRow
                      key={`separator-${rowIndex}`}
                      className="separator-row"
                      aria-hidden="true"
                    >
                      <TableCell /> {/* header column */}
                      <TableCell colSpan={row.cellCount - 1} />
                    </TableRow>
                  );
                }

                const { cells } = row;
                const sensorType = cells[0].type;
                const isFocusedRow = sensorType === focusedSensorType;

                return (
                  <TableRow
                    key={`body-row-${rowIndex}`}
                    className={isFocusedRow ? 'focused_row' : undefined}
                  >
                    {cells.map((cell, columnIndex) => (
                      <TableCell
                        key={`body-cell-${rowIndex}-${columnIndex}`}
                        sx={cell.tableCellSx}
                      >
                        {columnIndex > 0 && cell.value === EMPTY_DATA_VALUE ? (
                          <EmptyDataDash />
                        ) : (
                          cell.value
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};
